import React, { useState, useEffect, useRef } from 'react';
import DOMPurify from 'dompurify';
import apiClient from '../../api/client';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import PageTransitionWrapper from '../../components/layout/PageTransitionWrapper';
import { FaChurch, FaChevronDown, FaCheck } from 'react-icons/fa';

// ✅ Daftar sektor TIDAK lagi di-hardcode (PRESBITER_TABS dihapus).
// Diambil otomatis dari backend lewat GET /sektor — LEFT JOIN tabel `sektor`
// ke `presbiter` (dicocokkan lewat nama, karena tidak ada FK formal).
// Pakai leftJoin (bukan join biasa) supaya sektor BARU langsung muncul di
// dropdown walau belum ada baris presbiter pasangannya — slug-nya jadi null
// dan kontennya otomatis tampil "belum tersedia" tanpa perlu hit API.
//
// Response yang diharapkan dari getSektorList():
// [ { id, sektor: 'Anthiokia', slug: 'anthiokia' | null }, ... ]

// 🎨 Warna otomatis di-cycle berdasarkan urutan, TIDAK di-mapping manual per
// nama sektor — jadi sektor baru dari CRUD otomatis dapat warna juga.
const COLOR_PALETTE = [
    { color: 'text-indigo-600', bgColor: 'bg-indigo-50', border: 'border-indigo-600' },
    { color: 'text-red-500', bgColor: 'bg-red-50', border: 'border-red-500' },
    { color: 'text-teal-600', bgColor: 'bg-teal-50', border: 'border-teal-600' },
    { color: 'text-purple-600', bgColor: 'bg-purple-50', border: 'border-purple-600' },
    { color: 'text-pink-500', bgColor: 'bg-pink-50', border: 'border-pink-500' },
    { color: 'text-amber-600', bgColor: 'bg-amber-50', border: 'border-amber-600' },
    { color: 'text-blue-600', bgColor: 'bg-blue-50', border: 'border-blue-600' },
    { color: 'text-green-600', bgColor: 'bg-green-50', border: 'border-green-600' },
    { color: 'text-orange-600', bgColor: 'bg-orange-50', border: 'border-orange-600' },
    { color: 'text-cyan-600', bgColor: 'bg-cyan-50', border: 'border-cyan-600' },
    { color: 'text-rose-600', bgColor: 'bg-rose-50', border: 'border-rose-600' },
    { color: 'text-lime-600', bgColor: 'bg-lime-50', border: 'border-lime-600' },
];
const getColorForIndex = (index) => COLOR_PALETTE[index % COLOR_PALETTE.length];

const Presbiter = () => {
    // --- Daftar sektor dari backend ---
    const [sektorList, setSektorList] = useState([]);
    const [sektorLoaded, setSektorLoaded] = useState(false);
    const [sektorError, setSektorError] = useState(null);

    // --- Tab aktif sekarang berupa id sektor (selalu ada), BUKAN slug ---
    // (slug bisa null kalau sektor baru belum punya baris presbiter pasangannya)
    const [activeSektorId, setActiveSektorId] = useState(null);

    const [presbiterData, setPresbiterData] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const dropdownRef = useRef(null);

    // Tutup dropdown saat klik di luar area dropdown
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const getCleanBodyHtml = (rawHtml) => {
        if (!rawHtml) return '';
        return DOMPurify.sanitize(rawHtml, {
            FORBID_ATTR: ['style', 'class'],
            FORBID_TAGS: ['script'],
        });
    };

    // 1. Ambil daftar sektor dari BE (sekali saat mount)
    useEffect(() => {
        let isCancelled = false;
        const fetchSektorList = async () => {
            try {
                const response = await apiClient.getSektorList();
                const rawList = Array.isArray(response) ? response : (response?.data ?? []);
                // 🚫 Sektor "General" adalah kategori default/catch-all, bukan
                // sektor pelayanan sungguhan — jangan ditampilkan di dropdown.
                const list = rawList.filter(
                    (s) => (s.sektor || '').trim().toLowerCase() !== 'general'
                );

                if (!isCancelled) {
                    setSektorList(list);
                    if (list.length > 0) {
                        setActiveSektorId(list[0].id);
                    }
                }
            } catch (err) {
                console.error('Error fetching sektor list:', err);
                if (!isCancelled) {
                    setSektorList([]);
                    setSektorError('Gagal memuat daftar sektor.');
                }
            } finally {
                if (!isCancelled) setSektorLoaded(true);
            }
        };
        fetchSektorList();
        return () => { isCancelled = true; };
    }, []);

    // 2. Ambil isi/body presbiter setiap kali activeSektorId berubah
    useEffect(() => {
        if (!activeSektorId) return;

        const activeSektor = sektorList.find((s) => s.id === activeSektorId);

        // Sektor belum punya baris presbiter pasangannya (slug null)
        // → langsung tampilkan "belum tersedia", tidak perlu hit API.
        if (!activeSektor || !activeSektor.slug) {
            setPresbiterData(null);
            setError(null);
            setIsLoading(false);
            return;
        }

        let isCancelled = false;
        const fetchPresbiterData = async () => {
            setIsLoading(true);
            setError(null);
            try {
                const response = await apiClient.getPresbiter(activeSektor.slug);
                if (!isCancelled) setPresbiterData(response);
            } catch (err) {
                console.error('Error fetching presbiter data:', err);
                if (!isCancelled) setError('Gagal memuat data Presbiter. Silakan coba lagi nanti.');
            } finally {
                if (!isCancelled) setIsLoading(false);
            }
        };

        fetchPresbiterData();
        return () => { isCancelled = true; };
    }, [activeSektorId, sektorList]);

    const activeIndex = sektorList.findIndex((s) => s.id === activeSektorId);
    const activeSektor = activeIndex >= 0 ? sektorList[activeIndex] : null;
    const activeColors = getColorForIndex(activeIndex >= 0 ? activeIndex : 0);
    // 👇 "sektor.sektor" — sesuaikan kalau nama kolom di DB berbeda
    const activeName = activeSektor?.sektor || '';
    const activeFullName = activeSektor ? `Sektor Pelayanan ${activeSektor.sektor}` : '';

    return (
        <div className="bg-gray-50 min-h-screen py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-6xl mx-auto">

                {/* 🌟 Header Halaman */}
                <div className="bg-white rounded-2xl shadow-2xl p-6 sm:p-10 relative overflow-hidden border-b-8 border-green-600">
                    <div className="absolute inset-0 bg-gradient-to-r from-green-50/40 to-white transform skew-x-3 -translate-x-1/2 opacity-70"></div>
                    <div className="relative z-10 text-center">
                        <h1 className="text-4xl sm:text-5xl font-black text-gray-900 mb-2">
                            PRESBITER JEMAAT
                        </h1>
                        <p className="text-lg text-gray-600 max-w-3xl mx-auto font-medium">
                            Majelis Jemaat GPIB Kasih Karunia Badung Bali berdasarkan Sektor Pelayanan.
                        </p>
                    </div>
                </div>

                {/* 🗂️ Navigasi Dropdown (dari backend) */}
                <div className="mt-8 bg-white rounded-2xl shadow-xl p-4 sm:p-6 relative">
                    <h2 className="text-center font-bold text-gray-700 mb-4 border-b pb-2">Pilih Sektor Pelayanan</h2>

                    <div className="max-w-sm mx-auto relative" ref={dropdownRef}>
                        {/* Tombol Trigger Dropdown */}
                        <button
                            type="button"
                            onClick={() => setIsDropdownOpen((prev) => !prev)}
                            disabled={!sektorLoaded || sektorList.length === 0}
                            className={`w-full flex items-center justify-between gap-3 px-4 py-3 rounded-xl bg-white border-2 shadow-sm transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed
                                ${isDropdownOpen ? 'border-gray-400 ring-2 ring-gray-200' : 'border-gray-200 hover:border-gray-300'}`}
                        >
                            <span className="flex items-center gap-2 font-bold text-gray-800">
                                <span className={`text-lg ${activeColors.color}`}><FaChurch /></span>
                                {!sektorLoaded ? 'Memuat daftar sektor...' : (activeName || 'Pilih Sektor')}
                            </span>
                            <FaChevronDown
                                className={`text-gray-400 text-sm transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`}
                            />
                        </button>

                        {/* Daftar Pilihan Dropdown */}
                        {isDropdownOpen && sektorList.length > 0 && (
                            <div className="absolute z-20 mt-2 w-full bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden max-h-72 overflow-y-auto">
                                {sektorList.map((sektor, index) => {
                                    const isSelected = activeSektorId === sektor.id;
                                    const itemColors = getColorForIndex(index);
                                    return (
                                        <button
                                            key={sektor.id}
                                            type="button"
                                            onClick={() => {
                                                setActiveSektorId(sektor.id);
                                                setIsDropdownOpen(false);
                                            }}
                                            className={`w-full flex items-center justify-between gap-2 px-4 py-2.5 text-left text-sm font-semibold transition-colors duration-100
                                                ${isSelected ? 'bg-gray-100 text-gray-900' : 'text-gray-600 hover:bg-gray-50'}`}
                                        >
                                            <span className="flex items-center gap-2">
                                                <span className={itemColors.color}><FaChurch /></span>
                                                {sektor.sektor}
                                                {!sektor.slug && (
                                                    <span className="text-[10px] text-gray-400 italic">(belum ada data)</span>
                                                )}
                                            </span>
                                            {isSelected && <FaCheck className="text-gray-400 text-xs" />}
                                        </button>
                                    );
                                })}
                            </div>
                        )}

                        {sektorError && (
                            <p className="text-center text-xs text-red-500 font-semibold mt-2">{sektorError}</p>
                        )}
                    </div>
                </div>

                {/* 📋 Area Konten Utama */}
                <div className="mt-8 bg-white rounded-2xl shadow-xl p-6 sm:p-10 min-h-[400px] relative">
                    <div className="mb-6 border-b pb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                        <div>
                            <h2 className={`text-2xl font-black ${activeColors.color}`}>
                                {activeFullName}
                            </h2>
                            {presbiterData && presbiterData.title && (
                                <p className="text-sm text-gray-500 mt-1">{presbiterData.title}</p>
                            )}
                        </div>
                        <div className={`hidden sm:flex p-3 rounded-full ${activeColors.bgColor} ${activeColors.color} text-2xl`}>
                            <FaChurch />
                        </div>
                    </div>

                    {!sektorLoaded || isLoading ? (
                        <div className="absolute inset-0 flex items-center justify-center">
                            <LoadingSpinner />
                        </div>
                    ) : error ? (
                        <div className="text-center py-12">
                            <p className="text-red-500 font-semibold">{error}</p>
                        </div>
                    ) : sektorList.length === 0 ? (
                        <div className="text-center py-16 text-gray-400">
                            <p className="text-lg font-medium mb-1">Belum Ada Sektor Terdaftar</p>
                            <p className="text-sm">Daftar sektor belum tersedia dari server.</p>
                        </div>
                    ) : (
                        <PageTransitionWrapper>
                            {presbiterData && presbiterData.body ? (
                                <div
                                    className="komisi-body overflow-x-auto"
                                    dangerouslySetInnerHTML={{ __html: getCleanBodyHtml(presbiterData.body) }}
                                />
                            ) : (
                                <div className="text-center py-16 text-gray-400">
                                    <p className="text-lg font-medium mb-1">Susunan Presbiter Belum Tersedia</p>
                                    <p className="text-sm">Data Presbiter untuk sektor ini sedang dalam proses pembaharuan.</p>
                                </div>
                            )}
                        </PageTransitionWrapper>
                    )}
                </div>

            </div>
        </div>
    );
};

export default Presbiter;